import { GiPadlock, GiSandsOfTime } from "react-icons/gi";
import {
  BuildingCatalogEntry,
  BuildingState,
  BuildOrder,
} from "../types/castle";
import { BuildingPaintedIcon } from "./icons";
import { BuildingDescriptions } from "./buildingDescriptions";
import { formatDuration, secondsUntil } from "../lib/format";

interface Props {
  state: BuildingState;
  def: BuildingCatalogEntry | undefined;
  displayName: string;
  playerElement: number | undefined;
  activeOrder: BuildOrder | undefined;
  now: Date;
  castleLevel: number;
  maxAllowedWarHallLevel: number;
  onOpen: () => void;
}

// Row in the buildings list. Whole card is a single tap target — clicking
// anywhere opens the detail modal where the actual upgrade happens. The row
// itself shows: icon, name + level, short description + current-level
// effect (e.g. "Производство: 260/ч"). No inline build button — the user
// asked for that to live only in the detail modal so the list reads more
// like a status overview than a control panel.
export function BuildingRow({
  state,
  def,
  displayName,
  playerElement,
  activeOrder,
  now,
  castleLevel,
  maxAllowedWarHallLevel,
  onOpen,
}: Props) {
  const nextLevel = state.level + 1;
  const isUpgrading = !!activeOrder;
  const isMax = def ? state.level >= def.maxLevel : false;
  // WarHall has its own unlock ladder (Castle 1 / 10 / 20 + paid slot); the
  // server precomputes the current ceiling in maxAllowedWarHallLevel. Other
  // buildings follow the simple rule "next level must not exceed Castle".
  const isWarHall = state.type === 11;
  const warHallRequiredCastleLevel = (next: number) => {
    if (next <= 1) return 1;
    if (next === 2) return 10;
    return 20;
  };
  const requiredCastleLevel = isWarHall
    ? warHallRequiredCastleLevel(nextLevel)
    : nextLevel;
  const lockedByCastle = def
    ? isWarHall
      ? nextLevel > maxAllowedWarHallLevel
      : state.type !== 0 && nextLevel > castleLevel
    : false;

  const remaining = activeOrder ? secondsUntil(activeOrder.finishesAt, now) : 0;
  const progress = activeOrder
    ? (() => {
        const total =
          new Date(activeOrder.finishesAt).getTime() -
          new Date(activeOrder.startedAt).getTime();
        const elapsed = total - remaining * 1000;
        return total <= 0 ? 1 : Math.max(0, Math.min(1, elapsed / total));
      })()
    : 0;

  const description = BuildingDescriptions[state.type] ?? "";
  // Server-computed effect string for the player's current level. Empty when
  // the building hasn't been built yet (level=0) — we show the description
  // alone in that case so the row teases what the building does.
  const currentEffect =
    state.level > 0
      ? def?.levels.find((l) => l.level === state.level)?.effect
      : null;

  return (
    <button
      type="button"
      onClick={onOpen}
      data-tutorial={state.type === 0 ? "building-castle" : undefined}
      className="flex w-full items-center gap-3 rounded-xl bg-tg-secondaryBg p-3 text-left shadow-sm transition-colors active:bg-tg-bg"
    >
      <div className="relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-tg-bg text-tg-text">
        <BuildingPaintedIcon
          type={state.type}
          playerElement={playerElement}
          size={28}
        />
        {isUpgrading && (
          <svg
            className="pointer-events-none absolute inset-0 h-full w-full -rotate-90"
            viewBox="0 0 48 48"
            aria-hidden
          >
            <circle
              cx="24"
              cy="24"
              r="22"
              fill="none"
              stroke="currentColor"
              strokeOpacity="0.15"
              strokeWidth="2"
            />
            <circle
              cx="24"
              cy="24"
              r="22"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray={2 * Math.PI * 22}
              strokeDashoffset={2 * Math.PI * 22 * (1 - progress)}
              className="text-tg-accent transition-all duration-1000 ease-linear"
            />
          </svg>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="truncate font-medium">{displayName}</div>
          <div className="rounded bg-tg-bg px-1.5 py-0.5 text-xs text-tg-hint">
            Lvl {state.level}
            {def ? `/${def.maxLevel}` : ""}
          </div>
        </div>
        {isUpgrading ? (
          <div className="mt-1 flex items-center gap-1 text-xs text-tg-accent">
            <GiSandsOfTime size={12} />
            <span>
              → Lvl {activeOrder.targetLevel} · {formatDuration(remaining)}
            </span>
          </div>
        ) : (
          <>
            {description && (
              <div className="mt-0.5 line-clamp-2 text-xs leading-snug text-tg-hint">
                {description}
              </div>
            )}
            {currentEffect ? (
              <div className="mt-1 text-xs text-tg-accent">{currentEffect}</div>
            ) : isMax ? (
              <div className="mt-1 text-xs text-tg-hint">Максимальный уровень</div>
            ) : lockedByCastle ? (
              <div className="mt-1 flex items-center gap-1 text-xs text-tg-hint">
                <GiPadlock size={12} />
                <span>требует Замок Lvl {requiredCastleLevel}</span>
              </div>
            ) : null}
          </>
        )}
      </div>
      <div className="shrink-0 text-tg-hint">›</div>
    </button>
  );
}
